import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeftRight, Send, PackageCheck, Truck, CheckCircle2, XCircle, Search, Plus, Trash2, Clock } from "lucide-react";
import api from "../lib/api";
import { Card, Badge, Button, PageHeader, PageLoader, Modal, Select } from "../lib/ui";
import { JournalPreview } from "../components/JournalPreview";
import { fmtNum } from "../lib/constants";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";

const STATUS_TONE = { IN_TRANSIT: "warn", RECEIVED: "success", PARTIAL: "warn", CANCELLED: "mute" };
const fmtDate = (s) => (s ? new Date(s).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function Kpi({ icon: Icon, label, value, tone, testid }) {
  const tones = { warn: "bg-amber-50 text-amber-600", success: "bg-emerald-50 text-emerald-600", primary: "bg-primary-light text-primary" };
  return (
    <Card className="flex items-center gap-3 p-3" data-testid={testid}>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}><Icon size={18} /></span>
      <div className="min-w-0">
        <p className="font-heading text-xl font-extrabold leading-none text-ink">{value}</p>
        <p className="mt-0.5 truncate text-[11px] font-semibold uppercase text-ink-mute">{label}</p>
      </div>
    </Card>
  );
}

export default function StockTransfer() {
  const { t } = useT();
  const { user, hasPermission } = useAuth();
  const canSend = hasPermission("transfer_stock");
  const canReceive = hasPermission("receive_goods");

  const [transfers, setTransfers] = useState(null);
  const [send, setSend] = useState(false);
  const [receive, setReceive] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/transfers");
    setTransfers(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!transfers) return <PageLoader />;

  const inTransit = transfers.filter((x) => x.status === "IN_TRANSIT");
  const history = transfers.filter((x) => x.status !== "IN_TRANSIT");
  const totalItems = transfers.reduce((s, x) => s + (x.lines?.length || 0), 0);

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="stock-transfer">
      <PageHeader icon={ArrowLeftRight} title={t("Stock Transfer")} subtitle={t("Move ready-to-cook stock from Central Warehouse to a branch")}>
        {canSend && <Button onClick={() => setSend(true)} data-testid="st-open-send"><Send size={16} /> {t("Send to Branch")}</Button>}
      </PageHeader>

      <div className="mb-4 grid grid-cols-3 gap-3">
        <Kpi icon={Truck} label={t("In Transit")} value={inTransit.length} tone="warn" testid="st-kpi-transit" />
        <Kpi icon={CheckCircle2} label={t("Received")} value={transfers.filter((x) => x.status === "RECEIVED").length} tone="success" testid="st-kpi-received" />
        <Kpi icon={PackageCheck} label={t("Total Items Transferred")} value={totalItems} tone="primary" testid="st-kpi-items" />
      </div>

      {/* In transit — needs receiving */}
      <h3 className="mb-2 flex items-center gap-2 font-heading text-sm font-extrabold text-ink"><Truck size={15} className="text-amber-600" /> {t("Awaiting Receiving")} <Badge tone="warn">{inTransit.length}</Badge></h3>
      {inTransit.length === 0 ? (
        <Card className="mb-5 p-6 text-center text-sm text-ink-mute" data-testid="st-transit-empty">{t("No transfers in transit")}</Card>
      ) : (
        <div className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-2" data-testid="st-transit-list">
          {inTransit.map((x) => (
            <Card key={x.id} className="border-amber-200 bg-amber-50/40 p-4" data-testid={`st-transfer-${x.transfer_no}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-mono text-[11px] text-ink-mute">{x.transfer_no}</p>
                  <p className="font-heading text-base font-extrabold text-ink">→ {x.to_branch_name}</p>
                </div>
                <Badge tone={STATUS_TONE[x.status]}>{t(x.status)}</Badge>
              </div>
              <div className="mt-2 space-y-1">
                {x.lines.map((l) => (
                  <div key={l.product_id} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5 text-ink-soft">{l.is_rtc && <span className="rounded bg-emerald-100 px-1 text-[9px] font-bold text-emerald-700">Siap Masak</span>}{l.product_name}</span>
                    <span className="font-bold text-ink">{fmtNum(l.qty)} {l.unit}</span>
                  </div>
                ))}
              </div>
              <p className="mt-2 flex items-center gap-1 text-[11px] text-ink-mute"><Clock size={11} /> {fmtDate(x.sent_at)} · {x.created_by_name}</p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" className="flex-1" onClick={() => setDetail(x)} variant="outline" data-testid={`st-view-${x.transfer_no}`}>{t("Details")}</Button>
                {canReceive && <Button size="sm" className="flex-1" onClick={() => setReceive(x)} data-testid={`st-receive-${x.transfer_no}`}><PackageCheck size={14} /> {t("Receive")}</Button>}
                {canSend && <CancelBtn x={x} onDone={load} t={t} />}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* History */}
      <h3 className="mb-2 flex items-center gap-2 font-heading text-sm font-extrabold text-ink"><CheckCircle2 size={15} className="text-emerald-600" /> {t("Transfer History")}</h3>
      <Card data-testid="st-history">
        {history.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-mute">{t("No transfer history yet")}</p>
        ) : (
          <div className="divide-y divide-line">
            {history.map((x) => (
              <button key={x.id} onClick={() => setDetail(x)} data-testid={`st-history-${x.transfer_no}`} className="flex w-full items-center gap-3 py-3 text-left hover:bg-app">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-ink">→ {x.to_branch_name} <span className="font-mono text-[11px] text-ink-mute">· {x.transfer_no}</span></p>
                  <p className="text-xs text-ink-mute">{x.lines.length} {t("items")} · {fmtDate(x.received_at || x.sent_at)} · {x.received_by_name || x.created_by_name}</p>
                </div>
                <Badge tone={STATUS_TONE[x.status]}>{t(x.status)}</Badge>
              </button>
            ))}
          </div>
        )}
      </Card>

      {send && <SendModal onClose={() => setSend(false)} onDone={() => { setSend(false); load(); }} />}
      {receive && <ReceiveModal transfer={receive} onClose={() => setReceive(null)} onDone={() => { setReceive(null); load(); }} />}
      {detail && <DetailModal transfer={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function CancelBtn({ x, onDone, t }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try { await api.post(`/transfers/${x.id}/cancel`); toast.success(t("Transfer cancelled & central stock restored")); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };
  return <Button size="sm" variant="ghost" onClick={cancel} loading={busy} data-testid={`st-cancel-${x.transfer_no}`}><XCircle size={14} /> {t("Cancel")}</Button>;
}

/* ------------------------------ Send modal ------------------------------ */
function SendModal({ onClose, onDone }) {
  const { t } = useT();
  const toast = useToast();
  const [branches, setBranches] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]); // {product_id, name, unit, qty, available, is_rtc}
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
    api.get("/transfers/central-stock").then(({ data }) => setCatalog(data.items)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.filter((c) => c.current_stock > 0 && !cart.find((x) => x.product_id === c.product_id)
      && (!q || c.name.toLowerCase().includes(q) || (c.category_name || "").toLowerCase().includes(q)));
  }, [catalog, search, cart]);

  // Group filtered items by category → folder-like list (Master Data SSOT usability).
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const c of filtered.slice(0, 60)) {
      const key = c.category_id || "__uncat__";
      const label = c.category_name || "Uncategorized";
      if (!buckets.has(key)) buckets.set(key, { label, items: [] });
      buckets.get(key).items.push(c);
    }
    return [...buckets.entries()]
      .map(([key, g]) => ({ key, label: g.label, items: g.items.sort((a, b) => a.name.localeCompare(b.name)) }))
      .sort((a, b) => {
        if (a.key === "__uncat__") return 1;
        if (b.key === "__uncat__") return -1;
        return a.label.localeCompare(b.label);
      });
  }, [filtered]);

  const addLine = (c) => setCart((arr) => [...arr, { product_id: c.product_id, name: c.name, unit: c.unit, qty: "", available: c.current_stock, is_rtc: c.is_rtc }]);
  const setQty = (pid, v) => setCart((arr) => arr.map((x) => x.product_id === pid ? { ...x, qty: v } : x));
  const removeLine = (pid) => setCart((arr) => arr.filter((x) => x.product_id !== pid));

  const valid = branchId && cart.length > 0 && cart.every((x) => Number(x.qty) > 0 && Number(x.qty) <= x.available);

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await api.post("/transfers", { to_branch_id: branchId, notes, lines: cart.map((x) => ({ product_id: x.product_id, qty: Number(x.qty) })) });
      toast.success(t("Transfer dispatched"));
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? `${d.message}: ${d.current_stock} ${d.unit || ""}` : (d || t("Failed")));
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={t("Send to Branch")} size="lg"
      footer={<div className="flex items-center justify-between gap-3">
        <span className="text-xs text-ink-mute">{cart.length} {t("items")}</span>
        <Button onClick={submit} disabled={!valid} loading={busy} data-testid="st-send-submit"><Send size={16} /> {t("Dispatch Transfer")}</Button>
      </div>}>
      <div className="space-y-4" data-testid="st-send-modal">
        <Select label={t("Destination Branch")} value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="st-branch-select">
          <option value="">{t("Select branch…")}</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>

        {/* Cart */}
        {cart.length > 0 && (
          <div className="space-y-2 rounded-xl border border-line bg-app p-3" data-testid="st-cart">
            {cart.map((x) => {
              const over = Number(x.qty) > x.available;
              return (
                <div key={x.product_id} className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-ink">{x.is_rtc && <span className="mr-1 rounded bg-emerald-100 px-1 text-[9px] font-bold text-emerald-700">Siap Masak</span>}{x.name}</p>
                    <p className="text-[10px] text-ink-mute">{t("Available")}: {fmtNum(x.available)} {x.unit}</p>
                  </div>
                  <input type="number" min="0" step="0.01" value={x.qty} onChange={(e) => setQty(x.product_id, e.target.value)} data-testid={`st-qty-${x.product_id}`}
                    className={`w-24 rounded-lg border py-1.5 px-2 text-sm font-bold focus:outline-none ${over ? "border-danger" : "border-line focus:border-primary"}`} placeholder={x.unit} />
                  <button onClick={() => removeLine(x.product_id)} data-testid={`st-remove-${x.product_id}`} className="text-ink-mute hover:text-danger-text"><Trash2 size={16} /></button>
                </div>
              );
            })}
          </div>
        )}

        {/* Product picker */}
        <div>
          <div className="relative mb-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="st-product-search"
              placeholder={t("Search product or category…")} className="w-full rounded-xl border border-line bg-white py-2.5 pl-9 pr-3 text-sm focus:border-primary focus:outline-none" />
          </div>
          <div className="max-h-72 space-y-3 overflow-y-auto" data-testid="st-catalog">
            {grouped.length === 0 ? <p className="py-4 text-center text-xs text-ink-mute">{t("No products available")}</p> : grouped.map((g) => (
              <div key={g.key}>
                <div className="mb-1 px-1 text-[10px] font-bold uppercase tracking-wider text-ink-mute" data-testid={`st-cat-${g.label}`}>{g.label}</div>
                <div className="space-y-1">
                  {g.items.map((c) => (
                    <button key={c.product_id} onClick={() => addLine(c)} data-testid={`st-add-${c.product_id}`}
                      className="flex w-full items-center justify-between rounded-lg border border-line bg-white p-2 text-left hover:border-primary/50 hover:bg-app">
                      <span className="flex items-center gap-1.5 text-sm text-ink">{c.is_rtc && <span className="rounded bg-emerald-100 px-1 text-[9px] font-bold text-emerald-700">Siap Masak</span>}{c.name}</span>
                      <span className="flex items-center gap-2 text-xs text-ink-mute">{fmtNum(c.current_stock)} {c.unit} <Plus size={15} className="text-primary" /></span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="text-xs font-semibold text-ink-soft">{t("Notes")}</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="st-notes"
            className="mt-1 w-full rounded-xl border border-line py-2 px-3 text-sm focus:border-primary focus:outline-none" />
        </label>
        <p className="rounded-xl bg-blue-50 px-3 py-2 text-[11px] text-blue-700">{t("Central stock is deducted now. The branch confirms receiving to add it to branch inventory.")}</p>
      </div>
    </Modal>
  );
}

/* ----------------------------- Receive modal ----------------------------- */
function ReceiveModal({ transfer, onClose, onDone }) {
  const { t } = useT();
  const toast = useToast();
  const [lines, setLines] = useState(transfer.lines.map((l) => ({ ...l, received: String(l.qty) })));
  const [busy, setBusy] = useState(false);

  const setRecv = (pid, v) => setLines((arr) => arr.map((x) => x.product_id === pid ? { ...x, received: v } : x));
  const valid = lines.every((x) => Number(x.received) >= 0 && Number(x.received) <= x.qty);

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await api.post(`/transfers/${transfer.id}/receive`, { lines: lines.map((x) => ({ product_id: x.product_id, received_qty: Number(x.received) })) });
      toast.success(t("Transfer received into branch inventory"));
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`${t("Receive Transfer")} · ${transfer.transfer_no}`} size="md"
      footer={<div className="flex items-center justify-end"><Button onClick={submit} disabled={!valid} loading={busy} data-testid="st-receive-submit"><PackageCheck size={16} /> {t("Confirm Receiving")}</Button></div>}>
      <div className="space-y-3" data-testid="st-receive-modal">
        <p className="text-sm text-ink-soft">{t("Destination")}: <b className="text-ink">{transfer.to_branch_name}</b></p>
        <div className="space-y-2 rounded-xl border border-line bg-app p-3">
          {lines.map((l) => (
            <div key={l.product_id} className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-ink">{l.is_rtc && <span className="mr-1 rounded bg-emerald-100 px-1 text-[9px] font-bold text-emerald-700">Siap Masak</span>}{l.product_name}</p>
                <p className="text-[10px] text-ink-mute">{t("Sent")}: {fmtNum(l.qty)} {l.unit}</p>
              </div>
              <input type="number" min="0" max={l.qty} step="0.01" value={l.received} onChange={(e) => setRecv(l.product_id, e.target.value)} data-testid={`st-recv-${l.product_id}`}
                className="w-24 rounded-lg border border-line py-1.5 px-2 text-sm font-bold focus:border-primary focus:outline-none" />
            </div>
          ))}
        </div>
        <p className="rounded-xl bg-emerald-50 px-3 py-2 text-[11px] text-emerald-700">{t("Received quantity is added to this branch's inventory and becomes available for recipes & POS.")}</p>
      </div>
    </Modal>
  );
}

/* ------------------------------ Detail modal ------------------------------ */
function DetailModal({ transfer, onClose }) {
  const { t } = useT();
  return (
    <Modal open onClose={onClose} title={`${t("Transfer")} · ${transfer.transfer_no}`} size="md">
      <div className="space-y-3" data-testid="st-detail-modal">
        <div className="flex items-center gap-2">
          <Badge tone={STATUS_TONE[transfer.status]}>{t(transfer.status)}</Badge>
          <span className="ml-auto text-xs text-ink-mute">→ {transfer.to_branch_name}</span>
        </div>
        <div className="overflow-hidden rounded-xl border border-line">
          <table className="w-full text-sm">
            <thead className="bg-app text-[11px] uppercase text-ink-mute">
              <tr><th className="px-3 py-2 text-left">{t("Product")}</th><th className="px-3 py-2 text-right">{t("Sent")}</th><th className="px-3 py-2 text-right">{t("Received")}</th></tr>
            </thead>
            <tbody className="divide-y divide-line">
              {transfer.lines.map((l) => (
                <tr key={l.product_id}>
                  <td className="px-3 py-2 text-ink">{l.is_rtc && <span className="mr-1 rounded bg-emerald-100 px-1 text-[9px] font-bold text-emerald-700">Siap Masak</span>}{l.product_name}</td>
                  <td className="px-3 py-2 text-right font-bold text-ink">{fmtNum(l.qty)} {l.unit}</td>
                  <td className="px-3 py-2 text-right text-ink-soft">{l.received_qty != null ? `${fmtNum(l.received_qty)} ${l.unit}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-ink-mute">
          <p>{t("Dispatched")}: {fmtDate(transfer.sent_at)} · {transfer.created_by_name}</p>
          {transfer.received_at && <p>{t("Received")}: {fmtDate(transfer.received_at)} · {transfer.received_by_name}</p>}
          {transfer.notes && <p className="mt-1">{t("Notes")}: {transfer.notes}</p>}
        </div>
        <JournalPreview module="stock_transfer" docId={transfer.id} />
      </div>
    </Modal>
  );
}
